import { useEffect, useState } from 'react';
import { getSlipDraft, ParlayLeg, putSlipDraft, RaceChaos, SimModel, SimulationSlip, SlipDraft } from '../services/api';
import { getAccessToken } from '../services/authToken';

// The Slip Builder's bets, as drafts (what the editor holds), and the one slip remembered per browser (for the track
// it was built on). Shared by the Slip Builder, the Simulator's button count and the sidebar's "Add to Slip Builder".

export type LegType = 'win' | 'top3' | 'top5' | 'top10' | 'top20' | 'h2h' | 'group';
export type BetType = LegType | 'parlay';

export interface LegDraft {
  type: LegType;
  driver: string;
  others: string[];
}

export interface BetDraft {
  id: string;
  type: BetType;
  driver: string;
  others: string[];
  /** Parlay legs (type = 'parlay' only). */
  legs: LegDraft[];
  /** Parlay only: the bet wins if any leg hits (instead of every leg). */
  anyLeg?: boolean;
  oddsText: string;
  stakeText: string;
  /** The sportsbook the bet is at (one of utils/books.ts), if the user set it. */
  book?: string;
}

export const TOP_N: Partial<Record<BetType, number>> = { win: 1, top3: 3, top5: 5, top10: 10, top20: 20 };
/** Bets a slip holds (the editor, the check and the account copy all stop here; backend slips.MaxBets). */
export const MAX_SLIP_BETS = 12;
export const STORAGE_KEY = 'ltb.betSlip.v1';

let nextId = 1;
const newId = () => `b${Date.now().toString(36)}${nextId++}`;
export const emptyBet = (): BetDraft => ({
  id: newId(),
  type: 'top10',
  driver: '',
  others: [],
  legs: [],
  oddsText: '',
  stakeText: '',
});

export const legFromDraft = (l: { type: BetType; driver: string; others: string[] }): ParlayLeg | null => {
  if (!l.driver) return null;
  if (l.type === 'h2h' || l.type === 'group') {
    const others = l.others.filter((o) => o && o !== l.driver);
    if (others.length === 0 || (l.type === 'h2h' && others.length !== 1)) return null;
    return { kind: 'ahead_of', driver: l.driver, others };
  }
  return { kind: 'top_n', driver: l.driver, n: TOP_N[l.type] ?? 10 };
};

/** The bet's legs, or null if it isn't filled in yet. */
export const legsFor = (b: BetDraft): ParlayLeg[] | null => {
  if (b.type === 'parlay') {
    const legs = b.legs.map(legFromDraft);
    return legs.length >= 2 && legs.every(Boolean) ? (legs as ParlayLeg[]) : null;
  }
  const leg = legFromDraft(b);
  return leg ? [leg] : null;
};

export const draftFromLeg = (leg: ParlayLeg): LegDraft => {
  if (leg.kind === 'ahead_of') {
    const others = leg.others ?? [];
    return { type: others.length === 1 ? 'h2h' : 'group', driver: leg.driver, others };
  }
  const n = leg.n ?? 10;
  const type = (Object.keys(TOP_N) as LegType[]).find((t) => TOP_N[t] === n) ?? 'top10';
  return { type, driver: leg.driver, others: [] };
};

/** Bets on the remembered slip for this track (an empty slip or another track's slip counts as none). */
export const readSlip = (site: string): BetDraft[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (saved?.site === site && Array.isArray(saved.bets)) return saved.bets;
  } catch {
    /* storage unavailable */
  }
  return [];
};

const SLIP_EVENT = 'ltb-slip-change';

/** Save the slip in this browser. A change (not a re-write of the same slip) is also queued to sync to the account. */
export const writeSlip = (site: string, bets: BetDraft[], opts: { fromServer?: boolean } = {}) => {
  const text = JSON.stringify({ site, bets });
  try {
    if (localStorage.getItem(STORAGE_KEY) === text) return;
    localStorage.setItem(STORAGE_KEY, text);
  } catch {
    /* storage unavailable */
  }
  window.dispatchEvent(new Event(SLIP_EVENT));
  if (!opts.fromServer) markChanged(site);
};

/** A bet counts once it has anything in it (an untouched blank row doesn't). */
export const isFilled = (b: BetDraft) => !!(legsFor(b) || b.driver || b.legs.some((l) => l.driver) || b.oddsText);

/** Append a bet to this track's slip (replacing a slip from another track, and dropping blank rows). False, and
 * nothing added, when the slip is already full. */
export const addToSlip = (site: string, bet: Omit<BetDraft, 'id'>): boolean => {
  const bets = readSlip(site).filter(isFilled);
  if (bets.length >= MAX_SLIP_BETS) return false;
  writeSlip(site, [...bets, { ...bet, id: newId() }]);
  return true;
};

/** A priced market as a slip bet: one leg is a straight bet, several a parlay. Null for legs the editor can't hold. */
export const betFromLegs = (
  legs: ParlayLeg[],
  oddsText: string,
  stakeText = '',
  anyLeg = false,
): Omit<BetDraft, 'id'> | null => {
  if (
    legs.length === 0 ||
    legs.some((l) => l.kind === 'not_top_n' || (l.kind === 'top_n' && !TOP_N_VALUES.has(l.n ?? 10)))
  )
    return null;
  const drafts = legs.map(draftFromLeg);
  return legs.length === 1
    ? { ...drafts[0], legs: [], oddsText, stakeText }
    : { type: 'parlay', driver: '', others: [], legs: drafts, oddsText, stakeText, ...(anyLeg && { anyLeg: true }) };
};
const TOP_N_VALUES = new Set(Object.values(TOP_N));

/** How many bets are on this track's remembered slip, kept up to date. */
export const useSlipCount = (site: string): number => {
  const count = () => readSlip(site).filter(isFilled).length;
  const [n, setN] = useState(count);
  useEffect(() => {
    const update = () => setN(count());
    update();
    window.addEventListener(SLIP_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(SLIP_EVENT, update);
      window.removeEventListener('storage', update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);
  return n;
};

/**
 * A check of the slip under one model (the simulated outcomes and what they were checked against), kept with the slip
 * so reopening the Slip Builder shows the results without pricing it again. `ids`/`keys` say which bets (in which
 * order) it was checked with, `stakes` the stakes at check time, `model` the model it used.
 */
export interface StoredCheck {
  slip: SimulationSlip;
  keys: string[];
  ids: string[];
  stakes: number[];
  model: SimModel;
  /** The race chaos scenario the check used (absent = normal). */
  chaos?: RaceChaos;
}

/** The slip's last check under each model (Base and Track Specialists each keep their own). */
export type StoredChecks = Partial<Record<SimModel, StoredCheck>>;

const CHECK_KEY = 'ltb.betSlip.check.v2';
const OLD_CHECK_KEY = 'ltb.betSlip.check.v1'; // one check for the slip (before per-model checks)
// A big slip's outcomes are still small (at most a few thousand rows each); skip storing anything unexpectedly large.
const MAX_CHECK_CHARS = 900_000;

const isCheck = (c: any): c is StoredCheck =>
  !!c?.slip && Array.isArray(c.ids) && (c.model === 'base' || c.model === 'layered');

/**
 * Normalize a stored or synced check value to per-model checks: the current shape `{ v: 2, base?, layered? }`, or an
 * older single check (filed under the model it was made with).
 */
export const toChecks = (value: any): StoredChecks => {
  if (isCheck(value)) return { [value.model]: value };
  const out: StoredChecks = {};
  if (value && typeof value === 'object') {
    if (isCheck(value.base)) out.base = value.base;
    if (isCheck(value.layered)) out.layered = value.layered;
  }
  return out;
};

const hasChecks = (c: StoredChecks) => !!(c.base || c.layered);

export const readChecks = (site: string): StoredChecks => {
  try {
    const saved = JSON.parse(localStorage.getItem(CHECK_KEY) ?? 'null');
    if (saved?.site === site) return toChecks(saved.checks);
    const old = JSON.parse(localStorage.getItem(OLD_CHECK_KEY) ?? 'null');
    if (old?.site === site) return toChecks(old.check);
  } catch {
    /* storage unavailable */
  }
  return {};
};

export const writeChecks = (site: string, checks: StoredChecks, opts: { fromServer?: boolean } = {}) => {
  try {
    localStorage.removeItem(OLD_CHECK_KEY);
    const before = localStorage.getItem(CHECK_KEY);
    if (!hasChecks(checks)) {
      if (before === null || JSON.parse(before)?.site !== site) return;
      localStorage.removeItem(CHECK_KEY);
    } else {
      const text = JSON.stringify({ site, checks: { v: 2, ...checks } });
      if (before === text) return;
      if (text.length <= MAX_CHECK_CHARS) localStorage.setItem(CHECK_KEY, text);
      else localStorage.removeItem(CHECK_KEY);
    }
  } catch {
    return; /* storage unavailable or full */
  }
  if (!opts.fromServer) markChanged(site);
};

// ---------------------------------------------------------------------------------------------------------------
// Account sync (signed-in users): the slip and its last check are also saved to the backend (Firestore), so they
// follow the account across devices. The browser copy stays the working copy and the offline fallback.
//
// `base` is the server version the browser copy is based on; `dirty` means the browser has changes the server
// hasn't seen. Saves are debounced (2 s) and serialized. If another device saved first, the server copy wins and
// replaces the browser copy (the dialog then says the slip was updated from another device).

const SYNC_KEY = 'ltb.betSlip.sync.v1';
const PULLED_EVENT = 'ltb-slip-pulled';
const STATUS_EVENT = 'ltb-slip-sync-status';
const SAVE_DELAY_MS = 2000;

export type SyncStatus = 'off' | 'saving' | 'saved' | 'error';

interface SyncMeta {
  site: string;
  base: string | null;
  dirty: boolean;
}

const readMeta = (site: string): SyncMeta => {
  try {
    const m = JSON.parse(localStorage.getItem(SYNC_KEY) ?? 'null');
    if (m?.site === site) return { site, base: m.base ?? null, dirty: !!m.dirty };
  } catch {
    /* storage unavailable */
  }
  // No record for this track: anything already in the browser for it hasn't been saved to the account.
  return { site, base: null, dirty: readSlip(site).some(isFilled) };
};

const writeMeta = (m: SyncMeta) => {
  try {
    localStorage.setItem(SYNC_KEY, JSON.stringify(m));
  } catch {
    /* storage unavailable */
  }
};

let status: SyncStatus = 'off';
// Why the last save was refused (the server's reason), when it was; null for a network or server error.
let errorReason: string | null = null;
let enabled: boolean | null = null; // null until the backend has said whether it saves slips
const setStatus = (s: SyncStatus, reason: string | null = null) => {
  status = s;
  errorReason = reason;
  window.dispatchEvent(new Event(STATUS_EVENT));
};

const timers = new Map<string, ReturnType<typeof setTimeout>>();
let running: Promise<void> = Promise.resolve();

function markChanged(site: string) {
  writeMeta({ ...readMeta(site), dirty: true });
  if (enabled === false) return;
  clearTimeout(timers.get(site));
  timers.set(
    site,
    setTimeout(() => void syncSlip(site), SAVE_DELAY_MS),
  );
}

/** Put a server draft into the browser copy (bets and last check) and tell an open Slip Builder. */
const pull = (site: string, d: SlipDraft, fromOtherDevice: boolean) => {
  writeSlip(site, (Array.isArray(d.bets) ? d.bets : []) as BetDraft[], { fromServer: true });
  writeChecks(site, toChecks(d.check), { fromServer: true });
  writeMeta({ site, base: d.updatedAt, dirty: false });
  window.dispatchEvent(new CustomEvent(PULLED_EVENT, { detail: { site, fromOtherDevice } }));
};

const push = async (site: string, base: string | null) => {
  setStatus('saving');
  const res = await putSlipDraft(site, {
    bets: readSlip(site),
    check: hasChecks(readChecks(site)) ? { v: 2, ...readChecks(site) } : null,
    baseUpdatedAt: base,
  });
  if (res.conflict) pull(site, res.draft, true);
  else writeMeta({ site, base: res.draft.updatedAt, dirty: false });
  setStatus('saved');
};

const syncOnce = async (site: string) => {
  if (!(await getAccessToken())) return; // signed out: the browser copy is all there is
  const meta = readMeta(site);
  const { enabled: on, draft } = await getSlipDraft(site);
  enabled = on;
  if (!on) return setStatus('off');
  if (!draft) {
    if (readSlip(site).some(isFilled) || hasChecks(readChecks(site))) await push(site, null);
    else setStatus('saved');
  } else if (meta.dirty && meta.base === draft.updatedAt) {
    await push(site, draft.updatedAt); // only this browser changed it
  } else if (meta.base !== draft.updatedAt) {
    pull(site, draft, meta.base !== null || meta.dirty); // changed elsewhere (if both changed, the account copy wins)
    setStatus('saved');
  } else {
    setStatus('saved');
  }
};

/**
 * Bring this track's slip in line with the account: load a newer saved copy, or save the browser's changes. Safe to
 * call often (on page load, when the Slip Builder opens, after edits); calls run one at a time.
 */
export const syncSlip = (site: string): Promise<void> => {
  clearTimeout(timers.get(site));
  running = running
    .then(() => syncOnce(site))
    .catch((err) => {
      console.warn('Slip sync failed', err);
      setStatus('error', (err as { reason?: string })?.reason ?? null);
    });
  return running;
};

/** The account sync state, for a small "Saved" mark in the Slip Builder. */
export const useSlipSyncStatus = (): SyncStatus => {
  const [s, setS] = useState(status);
  useEffect(() => {
    const update = () => setS(status);
    window.addEventListener(STATUS_EVENT, update);
    return () => window.removeEventListener(STATUS_EVENT, update);
  }, []);
  return s;
};

/** Why the last save was refused (the server's reason), or null. */
export const useSlipSyncError = (): string | null => {
  const [r, setR] = useState(errorReason);
  useEffect(() => {
    const update = () => setR(errorReason);
    window.addEventListener(STATUS_EVENT, update);
    return () => window.removeEventListener(STATUS_EVENT, update);
  }, []);
  return r;
};

/** Run `onPull` when a newer copy of this track's slip arrives from the account. */
export const useSlipPulled = (site: string, onPull: (fromOtherDevice: boolean) => void) => {
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.site === site) onPull(!!d.fromOtherDevice);
    };
    window.addEventListener(PULLED_EVENT, handler);
    return () => window.removeEventListener(PULLED_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);
};
